import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, X, Sparkles, Loader2 } from "lucide-react";

const fmtDate = (v) => { try { return new Date(v).toLocaleString(); } catch { return String(v); } };

const emptyPost = { title: "", slug: "", excerpt: "", body: "", cover_url: "", tag: "HOFAVIPROC", published: true };

function slugify(s) {
  return String(s)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 120);
}

export default function AdminNews() {
  const [posts, setPosts] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);
  const [aiBullets, setAiBullets] = useState("");
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiProvider, setAiProvider] = useState("openai");

  async function aiDraft() {
    if (!form.title.trim()) {
      toast.error("Add a title first — the AI uses it as the anchor.");
      return;
    }
    setAiDrafting(true);
    try {
      const { data } = await api.post("/ai/draft-post", {
        title: form.title,
        bullets: aiBullets,
        tag: form.tag || "HOFAVIPROC",
        provider: aiProvider,
      });
      setForm((s) => ({ ...s, excerpt: data.excerpt, body: data.body }));
      toast.success(`Draft generated with ${aiProvider === "claude" ? "Claude" : "ChatGPT"} — edit as needed.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not draft the post.");
    } finally {
      setAiDrafting(false);
    }
  }

  async function load() {
    try {
      const { data } = await api.get("/admin/news");
      setPosts(data);
    } catch {
      setPosts([]);
    }
  }

  useEffect(() => { load(); }, []);

  function openNew() {
    setEditing(null);
    setForm(emptyPost);
    setShowForm(true);
  }
  function openEdit(p) {
    setEditing(p);
    setForm({
      title: p.title || "",
      slug: p.slug || "",
      excerpt: p.excerpt || "",
      body: p.body || "",
      cover_url: p.cover_url || "",
      tag: p.tag || "HOFAVIPROC",
      published: p.published !== false,
    });
    setShowForm(true);
  }
  function closeForm() {
    setShowForm(false);
    setEditing(null);
    setForm(emptyPost);
  }
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((s) => ({ ...s, [k]: v }));
  };

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug ? slugify(form.slug) : slugify(form.title) };
      if (editing) {
        await api.put(`/admin/news/${editing.id}`, payload);
        toast.success("Post updated.");
      } else {
        await api.post("/admin/news", payload);
        toast.success("Post published.");
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(p) {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/news/${p.id}`);
      toast.success("Post deleted.");
      load();
    } catch (err) {
      toast.error("Could not delete.");
    }
  }

  return (
    <div data-testid="admin-news-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl">News & Films</h3>
        <button onClick={openNew} data-testid="admin-news-new-btn" className="inline-flex items-center gap-2 bg-palm text-white font-bold rounded-full px-5 py-2.5 hover:shadow-xl transition">
          <Plus size={16} /> New post
        </button>
      </div>

      {posts === null && <div className="p-8 text-ink/60">Loading…</div>}
      {posts && posts.length === 0 && !showForm && (
        <div className="p-8 border border-dashed border-ink/15 rounded-[14px] text-ink/60 text-center">
          No posts yet. Publish your first story to feature it on the public News page.
        </div>
      )}
      {posts && posts.length > 0 && (
        <div className="overflow-auto border border-ink/10 rounded-[14px] bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-ink/5">
              <tr>
                <th className="text-left font-semibold p-3 border-b border-ink/10">Title</th>
                <th className="text-left font-semibold p-3 border-b border-ink/10">Slug</th>
                <th className="text-left font-semibold p-3 border-b border-ink/10">Tag</th>
                <th className="text-left font-semibold p-3 border-b border-ink/10">Published</th>
                <th className="text-left font-semibold p-3 border-b border-ink/10">Updated</th>
                <th className="text-right font-semibold p-3 border-b border-ink/10">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-b border-ink/5 hover:bg-mango/10">
                  <td className="p-3 align-top font-semibold">{p.title}</td>
                  <td className="p-3 align-top text-ink/70">{p.slug}</td>
                  <td className="p-3 align-top">{p.tag}</td>
                  <td className="p-3 align-top">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${p.published ? "bg-palm text-white" : "bg-ink/10 text-ink/70"}`}>
                      {p.published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="p-3 align-top text-ink/70">{fmtDate(p.updated_at || p.created_at)}</td>
                  <td className="p-3 align-top text-right">
                    <button onClick={() => openEdit(p)} data-testid={`admin-news-edit-${p.slug}`} className="text-palm font-bold text-sm mr-3 hover:underline">Edit</button>
                    <button onClick={() => remove(p)} data-testid={`admin-news-delete-${p.slug}`} className="text-berry inline-flex items-center gap-1 font-bold text-sm hover:underline"><Trash2 size={14} /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-ink/60 flex items-start justify-center overflow-auto p-6" data-testid="admin-news-form">
          <form onSubmit={save} className="bg-white rounded-[14px] w-full max-w-[720px] p-8 mt-10">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-2xl">{editing ? "Edit post" : "New post"}</h3>
              <button type="button" onClick={closeForm} data-testid="admin-news-close-btn" className="text-ink/60 hover:text-berry">
                <X size={22} />
              </button>
            </div>
            <label className="text-sm font-semibold block mb-1">Title</label>
            <input required value={form.title} onChange={set("title")} data-testid="admin-news-title" className="w-full rounded-md border border-ink/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-palm" />

            <div className="mb-3 bg-palm/5 border border-palm/15 rounded-[12px] p-3">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2 text-xs font-bold text-palm uppercase tracking-[0.14em]">
                  <Sparkles size={13} /> AI assist
                </div>
                <div className="flex gap-1.5" data-testid="admin-news-provider-picker">
                  {[{ key: "openai", label: "ChatGPT" }, { key: "claude", label: "Claude" }].map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setAiProvider(p.key)}
                      data-testid={`admin-news-provider-${p.key}`}
                      className={`px-3 py-1 rounded-full text-[11px] font-bold border-2 transition ${
                        aiProvider === p.key ? "bg-palm text-white border-palm" : "bg-white text-ink border-ink/15 hover:border-palm"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                value={aiBullets}
                onChange={(e) => setAiBullets(e.target.value)}
                placeholder="Optional bullets: who, what, where in District 17, what changed. (The AI will invent plausible community-grounded detail if left blank.)"
                rows={2}
                data-testid="admin-news-ai-bullets"
                className="w-full rounded-md border border-ink/15 px-3 py-2 text-sm focus:outline-none focus:border-palm"
              />
              <button
                type="button"
                onClick={aiDraft}
                disabled={aiDrafting}
                data-testid="admin-news-ai-draft-btn"
                className="mt-2 inline-flex items-center gap-2 bg-palm text-white font-bold rounded-full px-4 py-1.5 text-sm hover:shadow-xl transition disabled:opacity-60"
              >
                {aiDrafting ? <><Loader2 size={13} className="animate-spin" /> Drafting…</> : <><Sparkles size={13} /> Draft excerpt + body</>}
              </button>
            </div>

            <label className="text-sm font-semibold block mb-1">Slug <span className="text-ink/50 font-normal">(auto if blank)</span></label>
            <input value={form.slug} onChange={set("slug")} data-testid="admin-news-slug" placeholder="life-in-rural-liberia-ep-3" className="w-full rounded-md border border-ink/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-palm" />
            <label className="text-sm font-semibold block mb-1">Excerpt (max 400 chars)</label>
            <textarea required rows={2} maxLength={400} value={form.excerpt} onChange={set("excerpt")} data-testid="admin-news-excerpt" className="w-full rounded-md border border-ink/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-palm" />
            <label className="text-sm font-semibold block mb-1">Body (paragraphs separated by blank lines)</label>
            <textarea required rows={10} value={form.body} onChange={set("body")} data-testid="admin-news-body" className="w-full rounded-md border border-ink/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-palm font-mono text-sm" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold block mb-1">Cover image URL</label>
                <input value={form.cover_url} onChange={set("cover_url")} data-testid="admin-news-cover" placeholder="https://..." className="w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">Tag</label>
                <input value={form.tag} onChange={set("tag")} data-testid="admin-news-tag" className="w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
              </div>
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.published} onChange={set("published")} data-testid="admin-news-published" />
              Published (visible on public site)
            </label>
            <div className="mt-6 flex gap-3">
              <button disabled={saving} data-testid="admin-news-save-btn" className="bg-palm text-white font-bold rounded-full px-6 py-3 hover:shadow-xl transition disabled:opacity-60">
                {saving ? "Saving…" : editing ? "Save changes" : "Publish post"}
              </button>
              <button type="button" onClick={closeForm} className="border-2 border-ink/15 text-ink font-bold rounded-full px-6 py-3 hover:border-berry hover:text-berry transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
